// V3 approvals are one-time decisions; persistent grants are not consumed
// by the V3 runtime. Keep the current action explicit.
import { type ReactNode, useId } from 'react';

import { type WriteResultPresentation } from '@apps/web/features/phoebe_chat_v3/registry/types';
import { ActionResultCard } from '@apps/web/features/phoebe_chat_v3/ui/primitives/action_result_card';
import { ApprovalChangeDetails } from '@apps/web/features/phoebe_chat_v3/ui/primitives/approval_change_details';
import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';
import { cn } from '@apps/web/lib/utils';

export type ApprovalDecision = 'pending' | 'allowed' | 'denied';

export type GenericApprovalPanelProps = Readonly<{
    readonly title: string;
    readonly decision: ApprovalDecision;
    readonly outcome?: 'pending' | 'succeeded' | 'failed';
    readonly receipt?: WriteResultPresentation;
    /** Quiet suffix after the outcome label, e.g. "by you · just now". */
    readonly outcomeDetail?: string;
    /** Plain sentence under the question, while it is still a question. */
    readonly details?: string;
    readonly groups?: ReadonlyArray<{
        readonly heading: string;
        readonly items: ReadonlyArray<string>;
    }>;
    readonly onAllow: () => void;
    readonly onDeny: () => void;
    readonly disabled?: boolean;
    /** Inputs the approver can change before allowing. */
    readonly children?: ReactNode;
}>;

const actionButtonClassName = cn(
    'inline-flex min-h-8 items-center rounded-md px-3 text-v3-chat',
    'font-semibold outline-none focus-visible:ring-[3px]',
    'focus-visible:ring-ring/50 disabled:opacity-[0.42]',
);

export function GenericApprovalPanel({
    title,
    decision,
    outcome = 'pending',
    receipt,
    outcomeDetail,
    details,
    groups,
    onAllow,
    onDeny,
    disabled = false,
    children,
}: GenericApprovalPanelProps): ReactNode {
    const groupId = useId();
    const pending = decision === 'pending';
    const denied = decision === 'denied';
    const receiptFailed =
        outcome === 'succeeded' && receipt?.status === 'failed';
    const succeeded =
        decision === 'allowed' && outcome === 'succeeded' && !receiptFailed;
    const filteredGroups = groups?.flatMap((group) => {
        if (group.heading.trim().toLowerCase() === 'reason') {
            return [];
        }
        const items = group.items.filter(
            (item) => !/^reason\s*:/i.test(item.trim()),
        );
        return items.length > 0 ? [{ ...group, items }] : [];
    });
    const contextGroups: Array<{
        heading: string;
        items: ReadonlyArray<string>;
    }> = [];
    const changeGroups: Array<{
        heading: string;
        items: ReadonlyArray<string>;
    }> = [];
    for (const group of filteredGroups ?? []) {
        if (group.heading.trim().toLowerCase() === 'changes') {
            changeGroups.push(group);
            continue;
        }
        const fromItems = group.items.filter((item) =>
            item.startsWith('From: '),
        );
        const toItems = group.items.filter((item) => item.startsWith('To: '));
        if (fromItems.length === 1 && toItems.length === 1) {
            const contextItems = group.items.filter(
                (item) => item !== fromItems[0] && item !== toItems[0],
            );
            if (contextItems.length > 0) {
                contextGroups.push({ ...group, items: contextItems });
            }
            changeGroups.push({
                heading:
                    contextItems.length > 0
                        ? `${group.heading} changes`
                        : group.heading,
                items: [...fromItems, ...toItems],
            });
        } else {
            contextGroups.push(group);
        }
    }
    const displayGroups = [...contextGroups, ...changeGroups];
    const request = (
        <div
            className={cn(
                'w-full min-w-0 [overflow-wrap:anywhere]',
                pending && 'bg-card rounded-lg border px-3 py-2.5',
            )}
        >
            {pending ? (
                <h3 className="text-v3-chat m-0 min-w-0 font-semibold tracking-[-0.01em] text-pretty">
                    <ResolvedReferenceText text={title} />
                </h3>
            ) : null}
            {pending && details !== undefined && (
                <p className="text-muted-foreground text-v3-chat m-0 mt-1.5 max-w-[62ch]">
                    <ResolvedReferenceText text={details} />
                </p>
            )}
            {displayGroups?.map((group, groupIndex) => (
                <section
                    key={`${group.heading}-${groupIndex}`}
                    aria-labelledby={`${groupId}-${groupIndex}`}
                    className="mt-2"
                >
                    <h4
                        id={`${groupId}-${groupIndex}`}
                        className={cn(
                            'text-foreground m-0 text-v3-chat font-semibold',
                            group.heading.trim().toLowerCase() === 'changes' &&
                                'sr-only',
                        )}
                    >
                        <ResolvedReferenceText text={group.heading} />
                        {group.heading.trim().toLowerCase() === 'changes'
                            ? ': '
                            : null}
                    </h4>
                    {group.heading.trim().toLowerCase() === 'changes' ||
                    (group.items.some((item) => item.startsWith('From: ')) &&
                        group.items.some((item) => item.startsWith('To: '))) ? (
                        <div
                            className={cn(
                                group.heading.trim().toLowerCase() !==
                                    'changes' && 'mt-1.5',
                            )}
                        >
                            <ApprovalChangeDetails
                                items={group.items}
                                settingsChanges={
                                    group.heading.trim().toLowerCase() ===
                                    'changes'
                                }
                            />
                        </div>
                    ) : (
                        <ul className="text-muted-foreground text-v3-chat m-0 mt-1 list-disc space-y-1 pl-4">
                            {group.items.map((item, itemIndex) => (
                                <li
                                    key={`${groupIndex}-${itemIndex}`}
                                    className="whitespace-pre-wrap"
                                >
                                    <ResolvedReferenceText text={item} />
                                </li>
                            ))}
                        </ul>
                    )}
                </section>
            ))}
            {pending ? children : null}
            {pending ? (
                <div
                    aria-label="Approval choices"
                    className="mt-3 flex flex-wrap items-center gap-2"
                >
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onAllow}
                        className={cn(
                            actionButtonClassName,
                            'border-foreground bg-foreground text-background border hover:bg-foreground/85',
                        )}
                    >
                        Allow
                    </button>
                    <button
                        type="button"
                        disabled={disabled}
                        onClick={onDeny}
                        className={cn(
                            actionButtonClassName,
                            'text-muted-foreground hover:bg-accent border',
                        )}
                    >
                        Deny
                    </button>
                </div>
            ) : null}
        </div>
    );
    if (pending) {
        return request;
    }
    const status = denied
        ? 'denied'
        : outcome === 'pending'
          ? 'running'
          : receiptFailed
            ? 'failed'
            : outcome;
    return (
        <ActionResultCard
            status={status}
            title={
                denied
                    ? title
                    : outcome === 'pending'
                      ? title
                      : outcome === 'failed'
                        ? 'Couldn’t complete this action'
                        : (receipt?.title ?? title)
            }
            summary={
                denied
                    ? undefined
                    : outcome === 'pending'
                      ? undefined
                      : outcome === 'failed'
                        ? undefined
                        : receipt?.summary
            }
            notices={outcome === 'succeeded' ? receipt?.notices : undefined}
        >
            {outcomeDetail !== undefined && (
                <p className="text-muted-foreground text-v3-chat mt-2">
                    <ResolvedReferenceText text={outcomeDetail} />
                </p>
            )}
            {displayGroups?.length ? (
                <div className={cn(!succeeded && 'mt-3 border-t pt-2')}>
                    {request}
                </div>
            ) : null}
        </ActionResultCard>
    );
}
